/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/array-type */
import {SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Input, Table as TableAnt} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {processTypeFilter, TF} from '../../constants/Common';
import {EfdbName} from '../../constants/efdbNameConstants';
import {ISelect} from '../../interface/ISelect';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import processTypeJSON from '../../services/modules/JSON/processType';
import productsJSON from '../../services/modules/JSON/products';
import rawMaterialJson from '../../services/modules/JSON/rawMaterial';
import facilityDetails from '../../services/modules/OM/facilityDetails';
import productInfoAPI from '../../services/modules/PRODUCTS/ProductInfoAPI';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {hasUserRole, UserRole} from '../../utils/commonFunctions';
import {formatIndian} from '../../utils/formatIndian';
import mapAndFilter from '../../utils/mapCountry';
import DisplayModal from '../DisplayModal/DisplayModal';
import ProductFacilityDis from '../DisplayModal/ProductInfoDis/ProductFacilityDis';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import {yearKeyLabel} from '../Forms/Constants/IP/commonKeyLabel';
import {FacilityKey} from '../Forms/Constants/IP/FacilityKeyLabel';
import {
  ProductInfoKey,
  ProductInfoLabel
} from '../Forms/Constants/Products/ProductInfoKeyLabel';
import ProductInfoFormDrawerForm from '../Forms/ProductInfoFormDrawerForm';
import changeUnit from '../Forms/Utils/changeUnit';
import Loader from '../Loader/Loader';

import {productType, Title} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import ActionsButton from './Utils/ActionsButton';
import sumValue from './Utils/sumValue';
import TableTitleHeader from './Utils/TableTitleHeader';
import TimeFrameDataTooltip from './Utils/TimeFrameDataTooltip';

const ProductInfo: React.FC<{
  selectedYear: string;
}> = ({selectedYear}) => {
  interface DataType {
    key: React.Key;
    _id: string;
    fuelType: string;
    noOfUnits: number;
    deviceType: number;
    quantity: number;
    cost: number;
    driven: number;
  }
  const companyId = useCompanyStore.getState().companyId;

  const [dataSource, setDataSource] = useState<DataType[]>([]);

  const [showModel, setShowModel] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [openDisplayModal, setOpenDisplayModal] = useState(false);
  const [openDisplayModalData, setOpenDisplayModalData] = useState({});
  const [openDisplayModalType, setOpenDisplayModalType] = useState({
    title: '',
    dataKey: ''
  });
  const [tableType, setTableType] = useState<'facility' | 'raw'>('facility');
  const [allFacilityOpt, setAllFacilityOpt] = useState<any[]>([]);

  const [allJSON, setAllJSON] = useState(false);

  const products = useJSONStore.getState().products.filter((item) => {
    return item.dbName === EfdbName.eeio;
  });
  const rawMaterials = useJSONStore.getState().rawMaterials.filter((item) => {
    return item.dbName === EfdbName.eeio;
  });
  const processType = useJSONStore.getState().processType;

  const facilityDetail = useCompanyStore.getState().facilityDetails;

  useEffect(() => {
    if (selectedYear && allJSON) {
      setIsRefresh(true);
      productInfoAPI(
        APITypeEnum.GET_ALL,
        undefined,
        `${companyId}/${selectedYear}`
      )
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
        });
    }
  }, [selectedYear, allJSON]);

  useEffect(() => {
    // setIsRefresh(true);
    Promise.all([
      productsJSON(),
      facilityDetails(APITypeEnum.GET_ALL, false),
      rawMaterialJson(),
      processTypeFilter() ? processTypeJSON() : Promise.resolve()
    ])
      .catch(() => {})
      .finally(() => {
        // setIsRefresh(false);
        setAllJSON(true);
        setAllFacilityOpt(Object.values(facilityDetail).flat());
      });
  }, []);

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      productInfoAPI(
        APITypeEnum.GET_ALL,
        undefined,
        `${companyId}/${selectedYear}`
      )
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  const productInfoKeyLabel = useMemo(() => {
    return [
      {
        key: yearKeyLabel.key,
        label: yearKeyLabel.label,
        type: objTypes.string
      },
      {
        key: ProductInfoKey.PRODUCT_NAME,
        label: ProductInfoLabel.PRODUCT_NAME,
        type: objTypes.json,
        format: products
      },
      {
        key: ProductInfoKey.VOLUME,
        label: ProductInfoLabel.VOLUME,
        type: objTypes.productRelatedKey,
        format: products,
        relatedKey: ProductInfoKey.PRODUCT_NAME
      },
      {
        key: ProductInfoKey.PRODUCT_TYPE,
        label: ProductInfoLabel.PRODUCT_TYPE,
        type: objTypes.json,
        format: productType,
        isGreen: true
      },
      {
        key: ProductInfoKey.FACILITIES,
        label: `${ProductInfoLabel.FACILITIES} ${
          processTypeFilter() ? ` & its ${ProductInfoLabel.processType}` : ''
        }`,
        type: objTypes.arrayPair,
        format: [
          allFacilityOpt.map((item) => {
            return {label: item?.[FacilityKey.FACILITY_NAME], value: item?._id};
          }),
          processType
        ],
        renderFormat: processTypeFilter()
          ? [ProductInfoKey.SINGLE_FACILITY, ProductInfoKey.processType]
          : [ProductInfoKey.SINGLE_FACILITY],
        isGreen: true,
        renderType: 'multi'
      },
      {
        key: ProductInfoKey.RAW_MATERIAL_DATA,
        label: ProductInfoLabel.RAW_MATERIAL_DATA,
        type: objTypes.valueInPair,
        format: rawMaterials,
        renderFormat: [ProductInfoKey.RAW_MATERIAL_ID, ProductInfoKey.WEIGHT],
        isGreen: true,
        renderType: 'multi'
      },
      {
        key: TF,
        label: [
          ProductInfoLabel.UNIT_PRODUCED_TF,
          ProductInfoLabel.COST_TF,
          ProductInfoLabel.SELLING_TF
        ],
        type: objTypes.timeFrameData,
        renderType: 'multi'
      }
    ];
  }, [allFacilityOpt]);

  const handleDelete = (_id: string[] | string) => {
    productInfoAPI(APITypeEnum.DELETE_ALL, {
      productInfo: _id,
      year: selectedYear
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {})
      .finally(() => {});
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const SearchFilter = (
    title: string,
    dataIndex: keyof any,
    option: ISelect[] | undefined = undefined
  ): defaultCol & ColumnType<any> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        if (option) {
          const filtered = option.filter((item) => {
            if (typeof item?.label === 'string') {
              return item?.label
                .toString()
                .toLowerCase()
                .includes(value.toString().toLowerCase());
            } else {
              return [];
            }
          });

          return filtered?.some((item) => {
            return item?.value === record[dataIndex];
          });
        } else {
          return record[dataIndex]
            ?.toString()
            .toLowerCase()
            .includes(value?.toString().toLowerCase());
        }
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const columns: (defaultCol & ColumnsType<DataType>[0])[] = useMemo(() => {
    return [
      {
        title: ProductInfoLabel.PRODUCT_NAME,
        dataIndex: ProductInfoKey.PRODUCT_NAME,
        editable: true,
        type: 'select',
        ...SearchFilter(
          ProductInfoLabel.PRODUCT_NAME,
          ProductInfoKey.PRODUCT_NAME,
          products
        ),
        render: (value: any) => {
          return (
            <>
              {
                products.find((item) => {
                  return item.value === value;
                })?.label
              }
            </>
          );
        }
      },
      {
        title: ProductInfoLabel.VOLUME,
        dataIndex: ProductInfoKey.VOLUME,
        //   width: '120px',
        editable: true,
        type: 'number',
        render: (value: any, record: any) => {
          const mainValue = formatIndian(value);
          const unit = changeUnit(
            products.find((item) => {
              return (
                item?.value ===
                record?.[ProductInfoKey.PRODUCT_NAME as keyof DataType]
              );
            })?.state
          );

          return `${mainValue} ${
            mainValue === '-' ? '' : unit === 'NA' ? '' : unit
          }`;
        }
      },
      {
        title: ProductInfoLabel.PRODUCT_TYPE,
        dataIndex: ProductInfoKey.PRODUCT_TYPE,
        editable: true,
        filters: mapAndFilter(
          dataSource,
          productType,
          ProductInfoKey.PRODUCT_TYPE
        ).map((item) => {
          return {text: item.label, value: item.value};
        }),
        onFilter: (value: any, record: any) => {
          return (
            record?.[ProductInfoKey.PRODUCT_TYPE as keyof DataType] === value
          );
        },
        render: (value: string) => {
          return value.toUpperCase();
        }
      },
      {
        title: ProductInfoLabel.UNIT_PRODUCED_TF,
        dataIndex: TF,
        editable: true,
        type: 'select',
        render: (value: any) => {
          const mainData = value?.find((item: any) => {
            return item.type === ProductInfoKey.UNIT_PRODUCED_TF;
          });

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(mainData);
                setOpenDisplayModalType({
                  title: `${ProductInfoLabel.UNIT_PRODUCED_TF} (${
                    mainData?.unit ?? ''
                  })`,
                  dataKey: ProductInfoKey.UNIT_PRODUCED_TF
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${formatIndian(
                sumValue(mainData?.data)
              )} ${mainData?.unit}`}</div>
            </div>
          );
        }
      },
      {
        title: ProductInfoLabel.COST_TF,
        dataIndex: TF,
        editable: true,
        type: 'select',
        render: (value: any) => {
          const mainData = value?.find((item: any) => {
            return item.type === ProductInfoKey.COST_TF;
          });

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(mainData);
                setOpenDisplayModalType({
                  title: `${ProductInfoLabel.COST_TF} (${mainData?.unit})`,
                  dataKey: ProductInfoKey.COST_TF
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${mainData?.unit} ${String(
                formatIndian(sumValue(mainData.data))
              )}`}</div>
            </div>
          );
        }
      },
      {
        title: ProductInfoLabel.SELLING_TF,
        dataIndex: TF,
        editable: true,
        type: 'select',
        render: (value: any) => {
          const mainData = value?.find((item: any) => {
            return item.type === ProductInfoKey.SELLING_TF;
          });

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(mainData);
                setOpenDisplayModalType({
                  title: `${ProductInfoLabel.SELLING_TF} (${mainData?.unit})`,
                  dataKey: ProductInfoKey.SELLING_TF
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${mainData?.unit} ${String(
                formatIndian(sumValue(mainData.data))
              )}`}</div>
            </div>
          );
        }
      },
      {
        title: ProductInfoLabel.FACILITIES,
        dataIndex: ProductInfoKey.FACILITIES,
        render: (value: any) => {
          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(value);
                setOpenDisplayModalType({
                  title: ProductInfoLabel.FACILITIES,
                  dataKey: ProductInfoKey.FACILITIES
                });
                setTableType('facility');
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{value?.length}</div>
            </div>
          );
        }
      },
      {
        title: ProductInfoLabel.RAW_MATERIAL_DATA,
        dataIndex: ProductInfoKey.RAW_MATERIAL_DATA,
        render: (value: any) => {
          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(value);
                setOpenDisplayModalType({
                  title: ProductInfoLabel.RAW_MATERIAL_DATA,
                  dataKey: ProductInfoKey.RAW_MATERIAL_DATA
                });
                setTableType('raw');
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{value?.length}</div>
            </div>
          );
        }
      },
      ...(!hasUserRole(UserRole.View_Only)
        ? ([
            {
              title: Title.actionTitle,
              dataIndex: 'operation',
              type: '',
              fixed: 'right',
              width: '200px',
              render: (value: any, record: any) =>
                dataSource.length >= 1 ? (
                  <ActionsButton
                    record={record}
                    setShowModel={setShowModel}
                    setEditData={setEditData}
                    setIsEdit={setIsEdit}
                    handleDelete={handleDelete}
                    isConfigurator={true}
                  />
                ) : null
            }
          ] as any)
        : [])
    ];
  }, [dataSource]);

  return (
    <div>
      <TableTitleHeader
        title="Product Info"
        filter={{facilityId: '', selectedYear}}
        addButton={{
          setShowModel,
          setIsEdit,
          buttonTitle: 'Add Product'
        }}
        sampleDownload={{href: ''}}
        jobs={{setShowJobs: null, uploadType: ''}}
        deleteButton={{selectedRowKeys, handleDelete}}
        refresh={{setRefresh}}
        showAddOnly={true}
        onlyYear={true}
        shouldEdit={!hasUserRole(UserRole.View_Only)}
      />
      <TableAnt
        // getPopupContainer={(triggerNode) =>
        //   triggerNode.closest(
        //     '.ant-modal, .ant-drawer, [style*="position"]'
        //   ) as HTMLElement
        // }
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys,
          onChange: (selectedKey: React.Key[], selectedRow: DataType[]) => {
            setSelectedRowKeys(
              selectedRow.map((item) => {
                return item._id;
              })
            );
          }
        }}
        rowKey="_id"
        dataSource={dataSource}
        columns={columns as any}
        scroll={{x: 'auto'}}
        loading={{
          indicator: <Loader />,
          spinning: allJSON ? isRefresh : true
        }}
      />
      <DrawerForm
        open={showModel}
        onCancel={() => {
          setShowModel(false);
        }}
        setRefresh={setRefresh}
        data={isEdit ? editData : undefined}
        FormComponent={
          <ProductInfoFormDrawerForm
            data={isEdit ? editData : undefined}
            setSubmitDisabled={setSubmitDisabled}
          />
        }
        API={productInfoAPI}
        idKey={`${companyId}/${selectedYear}`}
        table={true}
        buttonDisabled={{value: submitDisabled, setter: setSubmitDisabled}}
        onlyYear={true}
        stepsTitle={[
          'Product & Raw Material Details',
          'Facilities',
          'Production Details'
        ]}
        formPreview={productInfoKeyLabel}
        isConfigurator={true}
      />

      <DisplayModal
        title={openDisplayModalType.title}
        open={openDisplayModal}
        onCancel={() => {
          setOpenDisplayModal(false);
        }}
        displayComponent={
          [
            ProductInfoLabel.COST_TF,
            ProductInfoLabel.SELLING_TF,
            ProductInfoLabel.UNIT_PRODUCED_TF
          ].some((label) => openDisplayModalType.title.includes(label)) ? (
            <TimeFrameDataTooltip mainData={openDisplayModalData} />
          ) : (
            <ProductFacilityDis
              dataSource={openDisplayModalData}
              type={tableType}
              facilities={allFacilityOpt}
            />
          )
        }
        isTable={
          ![
            ProductInfoLabel.COST_TF,
            ProductInfoLabel.SELLING_TF,
            ProductInfoLabel.UNIT_PRODUCED_TF
          ].some((label) => openDisplayModalType.title.includes(label))
        }
      />
    </div>
  );
};

export default ProductInfo;
